/** 汇聚/第三方支付拉起 - 作者: zbq, 日期: 2026-08-30 */
/**
 * - 微信公众号：WEIXIN_GZH + WeixinJSBridge；成功后 SPA 进 payscuess（禁止 location.replace）
 * - 支付宝：汇聚文档包装 alipays://…20000067&url=（系统浏览器）
 * - 微信内：不支持直接拉起支付宝，进 /alipay-open.html 提示「右上角···→在浏览器打开」
 * - 禁止：微信场景用 window.location 跳到第三方收银台（会「退出」H5）
 */

import { isWxBrowser } from '@/sheep/helper/utils';
import {
  ensureSiteDomain,
  getCachedPrimaryOrigin,
  isOnPromoHost,
  redirectToPayHostIfNeeded,
} from '@/sheep/helper/zq-site-domain';

/** 汇聚文档固定前缀 */
const ALIPAY_SCHEME_PREFIX = 'alipays://platformapi/startapp?appId=20000067&url=';

/** 支付中订单，用于 pageshow 回前台后继续确认（防微信收银台关闭 webview 丢状态） */
const PENDING_PAY_KEY = 'zq_pending_pay';
/** 付完若被微信踢回，再进 H5 时自动回到方案详情 */
const PAY_RESUME_KEY = 'zq_pay_resume';

let wxStayArmed = false;
let wxStayPopHandler = null;
let wxStayTimer = null;

export function buildAlipaySchemeUrl(h5Url) {
  if (!h5Url) return '';
  const u = String(h5Url).trim();
  if (u.startsWith('alipays://')) return u;
  if (u.startsWith('<') || u.toLowerCase().includes('<html')) return '';
  if (!/^https?:\/\//i.test(u)) return '';
  return ALIPAY_SCHEME_PREFIX + encodeURIComponent(u);
}

/**
 * Android 微信内 alipays:// 常被吞，需用 intent 指定支付宝包名（对接文档 APP 侧 Intent 同理）
 */
export function buildAlipayIntentUrl(h5UrlOrScheme) {
  const scheme = String(h5UrlOrScheme || '').startsWith('alipays://')
    ? String(h5UrlOrScheme).trim()
    : buildAlipaySchemeUrl(h5UrlOrScheme);
  if (!scheme) return '';
  const path = scheme.replace(/^alipays:\/\//i, '');
  return `intent://${path}#Intent;scheme=alipays;package=com.eg.android.AlipayGphone;end`;
}

/**
 * 微信屏蔽自定义 Scheme：经支付宝官方 HTTPS 中转再唤起 App
 * （文档「直接跳 alipays://」在系统浏览器有效；微信内必须中转）
 */
export function wrapAlipaySchemeForWechat(scheme) {
  if (!scheme) return '';
  const s = String(scheme).startsWith('alipays://')
    ? String(scheme).trim()
    : buildAlipaySchemeUrl(scheme);
  if (!s) return '';
  return `https://ds.alipay.com/?scheme=${encodeURIComponent(s)}`;
}

/** 当前环境应使用的唤起链接 */
export function resolveAlipayOpenHref(schemeOrHttps) {
  const scheme = String(schemeOrHttps || '').startsWith('alipays://')
    ? String(schemeOrHttps).trim()
    : buildAlipaySchemeUrl(schemeOrHttps);
  if (!scheme) return '';
  // #ifdef H5
  if (isWxBrowser()) {
    return wrapAlipaySchemeForWechat(scheme);
  }
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (/Android/i.test(ua)) {
    return buildAlipayIntentUrl(scheme);
  }
  // #endif
  return scheme;
}

export function resolvePayScene(payType) {
  if (payType === 'alipay' || payType === '5') return 'alipay';
  if (payType === 'wechat' || payType === '3' || payType === 'wx') {
    return 'wx_gzh';
  }
  return '';
}

export function getStoredOpenId() {
  return (
    uni.getStorageSync('openid') ||
    uni.getStorageSync('openId') ||
    uni.getStorageSync('wx_openid') ||
    ''
  );
}

export function isPendingPayResult(data) {
  if (!data) return false;
  if (data.jsapiParams && (data.jsapiParams.appId || data.jsapiParams.paySign)) return true;
  if (data.alipaySchemeUrl) return true;
  if (data.pending && data.payUrl) return true;
  return !!data.payUrl;
}

export function markPendingPay(ctx = {}) {
  const payload = {
    orderId: ctx.orderId || '',
    articleId: ctx.articleId || '',
    at: Date.now(),
  };
  try {
    uni.setStorageSync(PENDING_PAY_KEY, payload);
  } catch (e) {
    /* ignore */
  }
}

export function clearPendingPay() {
  try {
    uni.removeStorageSync(PENDING_PAY_KEY);
  } catch (e) {
    /* ignore */
  }
}

export function peekPendingPay() {
  try {
    return uni.getStorageSync(PENDING_PAY_KEY) || null;
  } catch (e) {
    return null;
  }
}

/** 记录刚支付的方案，供被微信踢回后重新进入时自动打开 */
export function markPayResume(ctx = {}) {
  const articleId = ctx.articleId || '';
  const payload = {
    articleId: articleId ? String(articleId) : '',
    orderId: ctx.orderId || '',
    to: ctx.to || 'home',
    at: Date.now(),
  };
  // 无方案也可记 to=home，方便踢回后回首页
  if (!payload.articleId && payload.to !== 'home') return;
  try {
    uni.setStorageSync(PAY_RESUME_KEY, payload);
  } catch (e) {
    /* ignore */
  }
  // #ifdef H5
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(PAY_RESUME_KEY, JSON.stringify(payload));
      sessionStorage.setItem(PAY_RESUME_KEY, JSON.stringify(payload));
    }
  } catch (e) {
    /* ignore */
  }
  // #endif
}

/** 读取并消费最近支付恢复目标（10 分钟内有效） */
export function consumePayResume(maxAgeMs = 10 * 60 * 1000) {
  try {
    const raw = peekPayResume(maxAgeMs);
    if (!raw) return null;
    uni.removeStorageSync(PAY_RESUME_KEY);
    return raw;
  } catch (e) {
    return null;
  }
}

/** 仅查看，不消费 */
export function peekPayResume(maxAgeMs = 10 * 60 * 1000) {
  try {
    const raw = uni.getStorageSync(PAY_RESUME_KEY);
    if (!raw?.articleId) return null;
    if (Date.now() - Number(raw.at || 0) > maxAgeMs) {
      uni.removeStorageSync(PAY_RESUME_KEY);
      return null;
    }
    return raw;
  } catch (e) {
    return null;
  }
}

/**
 * 支付成功后拦截微信内「返回」把 webview 直接退出到聊天。
 * 无法阻止微信彻底杀掉 Activity，但可挡住收银层收起后的一次 history.back。
 */
export function armWxPayStayInH5(ms = 90000) {
  // #ifdef H5
  if (typeof window === 'undefined' || !window.history?.pushState) return;
  disarmWxPayStayInH5();
  try {
    const href = window.location.href;
    window.history.pushState({ zqPayStay: 1 }, '', href);
    window.history.pushState({ zqPayStay: 2 }, '', href);
  } catch (e) {
    /* ignore */
  }
  wxStayPopHandler = () => {
    try {
      window.history.pushState({ zqPayStay: 1 }, '', window.location.href);
    } catch (e) {
      /* ignore */
    }
  };
  window.addEventListener('popstate', wxStayPopHandler);
  wxStayArmed = true;
  wxStayTimer = setTimeout(() => disarmWxPayStayInH5(), ms);
  // #endif
}

export function disarmWxPayStayInH5() {
  // #ifdef H5
  if (wxStayTimer) {
    clearTimeout(wxStayTimer);
    wxStayTimer = null;
  }
  if (wxStayPopHandler && typeof window !== 'undefined') {
    window.removeEventListener('popstate', wxStayPopHandler);
    wxStayPopHandler = null;
  }
  wxStayArmed = false;
  // #endif
}

/**
 * 微信支付成功落地：静态 /pay-done.html（非 SPA）。
 * JSAPI 回调里立刻 location.href 过去，抢在微信关 webview 之前占住页面。
 */
export function buildPayDoneHref(ctx = {}) {
  // 支付完成页固定主站，避免推广域与微信授权域不一致
  let origin = getCachedPrimaryOrigin() || '';
  if (
    !origin &&
    typeof window !== 'undefined' &&
    window.location?.origin
  ) {
    origin = window.location.origin;
  }
  const q = [];
  q.push('from=joinpay');
  if (ctx.orderId) q.push(`orderId=${encodeURIComponent(ctx.orderId)}`);
  if (ctx.articleId) q.push(`articleId=${encodeURIComponent(ctx.articleId)}`);
  return `${origin}/pay-done.html?${q.join('&')}`;
}

export function buildPayReturnPath(ctx = {}) {
  const orderId = ctx.orderId || '';
  const articleId = ctx.articleId || '';
  let path = '/pages/mine/payscuess?from=joinpay';
  if (orderId) path += `&orderId=${encodeURIComponent(orderId)}`;
  if (articleId) path += `&articleId=${encodeURIComponent(articleId)}`;
  return path;
}

export function buildPayReturnHref(ctx = {}) {
  return buildPayDoneHref(ctx);
}

/** 支付完成后进静态成功页，再回首页 */
export function goPaySuccessInApp(ctx = {}) {
  markPayResume({ ...ctx, to: 'home' });
  clearPendingPay();
  const href = ctx.successUrl || buildPayDoneHref(ctx);
  // #ifdef H5
  try {
    window.location.href = href;
    return;
  } catch (e) {
    /* fall through */
  }
  // #endif
  uni.reLaunch({ url: '/pages/tabs/home' });
}

export function openJoinPayUrl(payUrl) {
  if (!payUrl) {
    uni.showToast({ title: '支付地址无效', icon: 'none' });
    return false;
  }
  // #ifdef H5
  // 微信内跳第三方收银台会离开当前 H5，禁止用于微信支付
  if (isWxBrowser() && /wx|weixin|wechat/i.test(String(payUrl))) {
    uni.showToast({ title: '请使用微信内公众号支付', icon: 'none' });
    return false;
  }
  window.location.href = payUrl;
  // #endif
  // #ifndef H5
  if (typeof plus !== 'undefined' && plus.runtime?.openURL) {
    plus.runtime.openURL(payUrl);
  } else {
    uni.setClipboardData({
      data: payUrl,
      success: () => uni.showToast({ title: '支付链接已复制，请在浏览器打开', icon: 'none' }),
    });
  }
  // #endif
  return true;
}

/**
 * @param {object} data 支付接口返回
 * @param {{ orderId?: string, articleId?: string, stayInApp?: boolean }} [options]
 * @returns {Promise<boolean>} true=已成功调起且（微信）已支付成功；支付宝为已跳转中转页
 */
export async function launchJoinPay(data, options = {}) {
  if (!data) {
    uni.showToast({ title: '支付参数无效', icon: 'none' });
    return false;
  }

  // 推广域落地：仅微信支付跳主站（登录已留在推广域；须重新点支付）
  // #ifdef H5
  try {
    await ensureSiteDomain();
    if (data.jsapiParams && isOnPromoHost()) {
      uni.showToast({ title: '即将跳转支付域名…', icon: 'none' });
      redirectToPayHostIfNeeded();
      return false;
    }
  } catch (e) {
    /* ignore */
  }
  // #endif

  const orderId = options.orderId || resolveOrderId(data);
  const articleId = options.articleId || data.articleId || data.order?.articleId || '';
  const stayInApp = options.stayInApp !== false;
  const payCtx = { orderId, articleId, successUrl: options.successUrl || '' };

  if (data.jsapiParams) {
    markPendingPay(payCtx);
    markPayResume(payCtx);
    // 先算好落地页，回调里第一时间跳转（比 await 后再跳更稳）
    const doneHref = payCtx.successUrl || buildPayDoneHref(payCtx);
    const paid = await invokeWxJsapiPay(data.jsapiParams, doneHref);
    if (paid) {
      // 若回调里已 location，这里是兜底
      goPaySuccessInApp(payCtx);
      return true;
    }
    clearPendingPay();
    return false;
  }

  const channel = String(data.payChannel || '').toLowerCase();
  const payType = String(data.payType || '').toLowerCase();
  const isAlipay =
    channel === 'alipay' ||
    payType === 'alipay' ||
    payType === '5' ||
    !!data.alipaySchemeUrl;
  const isWechat =
    channel === 'jsapi' ||
    channel === 'wx_gzh' ||
    payType === 'wechat' ||
    payType === '3' ||
    payType === 'wx';

  if (isAlipay) {
    markPendingPay(payCtx);
    return launchAlipay(data);
  }

  // 微信必须走 JSAPI；缺参时绝不 location 跳出 H5
  if (isWechat || isWxBrowser()) {
    uni.showToast({
      title: '微信支付参数缺失，请重新微信授权后再试',
      icon: 'none',
    });
    return false;
  }

  markPendingPay(payCtx);
  return openJoinPayUrl(data.payUrl);
}

function resolveOrderId(data) {
  return data.orderId || data.id || data.order?.id || '';
}

function launchAlipay(data) {
  const httpsUrl =
    data.payUrl && /^https?:\/\//i.test(String(data.payUrl).trim())
      ? String(data.payUrl).trim()
      : '';
  let scheme = data.alipaySchemeUrl || '';
  if (!scheme || scheme.startsWith('http')) {
    scheme = buildAlipaySchemeUrl(httpsUrl || data.payUrl);
  }
  if (!scheme && !httpsUrl) {
    uni.showToast({ title: '支付宝支付地址无效', icon: 'none' });
    return false;
  }

  const orderId = resolveOrderId(data);

  // #ifdef H5
  try {
    const payload = {
      scheme: scheme || '',
      httpsUrl: httpsUrl || '',
      orderId: orderId || '',
    };
    try {
      uni.setStorageSync('zq_alipay_jump', payload);
    } catch (e) {
      /* ignore */
    }
    try {
      sessionStorage.setItem('zq_alipay_jump', JSON.stringify(payload));
      localStorage.setItem('zq_alipay_jump', JSON.stringify(payload));
    } catch (e) {
      /* ignore */
    }
    const q = [
      't=' + Date.now(),
      httpsUrl ? 'u=' + encodeURIComponent(httpsUrl) : '',
      orderId ? 'oid=' + encodeURIComponent(orderId) : '',
    ]
      .filter(Boolean)
      .join('&');
    window.location.href = `/alipay-open.html?${q}`;
    return true;
  } catch (e) {
    console.error('[joinpay] alipay launch fail', e);
    uni.showToast({ title: '打开支付宝失败', icon: 'none' });
    return false;
  }
  // #endif

  return openJoinPayUrl(scheme || httpsUrl);
}

/**
 * 唤起支付宝：微信用 ds.alipay.com 中转；其它环境 a.click + location
 */
export function openAlipayScheme(schemeOrHttps) {
  const href = resolveAlipayOpenHref(schemeOrHttps);
  if (!href) return false;
  // #ifdef H5
  try {
    const a = document.createElement('a');
    a.setAttribute('href', href);
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      try {
        document.body.removeChild(a);
      } catch (e) {
        /* ignore */
      }
    }, 100);
  } catch (e) {
    /* ignore */
  }
  try {
    window.location.href = href;
  } catch (e) {
    console.error('[joinpay] open scheme fail', e);
  }
  // #endif
  // #ifndef H5
  openJoinPayUrl(href);
  // #endif
  return true;
}

function invokeWxJsapiPay(raw, doneHref = '') {
  // #ifdef H5
  const params = normalizeJsapiParams(raw);
  if (!params) {
    uni.showToast({ title: '微信支付参数无效', icon: 'none' });
    return Promise.resolve(false);
  }
  if (!isWxBrowser()) {
    uni.showToast({ title: '请在微信内打开完成支付', icon: 'none' });
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const onBridgeReady = () => {
      try {
        // eslint-disable-next-line no-undef
        WeixinJSBridge.invoke('getBrandWCPayRequest', params, (res) => {
          const msg = (res && (res.err_msg || res.errMsg)) || '';
          if (msg.indexOf(':ok') >= 0) {
            // 回调同步跳静态页，抢在微信点「完成」关 webview 之前
            if (doneHref) {
              try {
                window.location.href = doneHref;
              } catch (e) {
                /* ignore */
              }
            }
            resolve(true);
            return;
          }
          if (msg.indexOf(':cancel') >= 0) {
            uni.showToast({ title: '已取消支付', icon: 'none' });
            resolve(false);
            return;
          }
          uni.showToast({ title: '支付未完成', icon: 'none' });
          resolve(false);
        });
      } catch (e) {
        console.error('[joinpay] WeixinJSBridge invoke fail', e);
        uni.showToast({ title: '调起微信支付失败', icon: 'none' });
        resolve(false);
      }
    };

    // eslint-disable-next-line no-undef
    if (typeof WeixinJSBridge === 'undefined') {
      if (document.addEventListener) {
        document.addEventListener('WeixinJSBridgeReady', onBridgeReady, false);
      } else if (document.attachEvent) {
        document.attachEvent('WeixinJSBridgeReady', onBridgeReady);
        document.attachEvent('onWeixinJSBridgeReady', onBridgeReady);
      }
      setTimeout(() => {
        // eslint-disable-next-line no-undef
        if (typeof WeixinJSBridge === 'undefined') {
          uni.showToast({ title: '微信环境未就绪，请刷新重试', icon: 'none' });
          resolve(false);
        }
      }, 8000);
    } else {
      onBridgeReady();
    }
  });
  // #endif

  // #ifndef H5
  uni.showToast({ title: '当前端不支持微信公众号支付', icon: 'none' });
  return Promise.resolve(false);
  // #endif
}

function normalizeJsapiParams(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const appId = raw.appId || raw.appid;
  const timeStamp = String(raw.timeStamp || raw.timestamp || '');
  const nonceStr = raw.nonceStr || raw.noncestr;
  const pkg = raw.package || raw.packageValue;
  const signType = raw.signType || 'MD5';
  const paySign = raw.paySign;
  if (!appId || !timeStamp || !nonceStr || !pkg || !paySign) return null;
  return { appId, timeStamp, nonceStr, package: pkg, signType, paySign };
}
